/**
 * Admin Promotions Page
 *
 * Create and manage promotional credit campaigns
 */

import { useEffect, useState } from 'react';
import { Logger, LogCategory } from '@/utils/logger';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { useAdminStore, usePromotions, useAllCompanies, usePromotionApplications } from '@/stores/adminStore';
import type { Promotion } from '@/types';
import {
  Tag,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Gift,
  TrendingUp,
} from 'lucide-react';

export function AdminPromotionsPage() {
  const { success: showSuccess, error: showError } = useToast();
  const {
    fetchPromotions,
    fetchAllCompanies,
    fetchPromotionApplications,
    createPromotion,
    updatePromotion,
    deletePromotion,
    applyPromotion,
  } = useAdminStore();

  const promotions = usePromotions();
  const companies = useAllCompanies();
  const applications = usePromotionApplications();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [deletingPromo, setDeletingPromo] = useState<Promotion | null>(null);
  const [applyingPromo, setApplyingPromo] = useState<Promotion | null>(null);

  // Form states
  const [promoForm, setPromoForm] = useState({
    code: '',
    name: '',
    description: '',
    value: '',
    isActive: true,
  });
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  useEffect(() => {
    Logger.info(LogCategory.UI, 'AdminPromotionsPage mounted', 'AdminPromotionsPage');
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchPromotions(),
          fetchAllCompanies(),
          fetchPromotionApplications(),
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [fetchPromotions, fetchAllCompanies, fetchPromotionApplications]);

  const handleCreate = async () => {
    if (!promoForm.code || !promoForm.name || !promoForm.value) {
      showError('Error', 'Please fill in all required fields');
      return;
    }

    const value = parseInt(promoForm.value);
    if (isNaN(value) || value <= 0) {
      showError('Error', 'Please enter a valid credit value');
      return;
    }

    setIsSaving(true);
    try {
      await createPromotion({
        code: promoForm.code.toUpperCase(),
        name: promoForm.name,
        description: promoForm.description,
        type: 'free_credits',
        value,
        isActive: promoForm.isActive,
      });

      setShowCreateModal(false);
      resetForm();
      showSuccess('Success', 'Promotion created');
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to create promotion');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingPromo) return;

    if (!promoForm.code || !promoForm.name || !promoForm.value) {
      showError('Error', 'Please fill in all required fields');
      return;
    }

    const value = parseInt(promoForm.value);
    if (isNaN(value) || value <= 0) {
      showError('Error', 'Please enter a valid credit value');
      return;
    }

    setIsSaving(true);
    try {
      await updatePromotion(editingPromo.id, {
        code: promoForm.code.toUpperCase(),
        name: promoForm.name,
        description: promoForm.description,
        value,
        isActive: promoForm.isActive,
      });

      setEditingPromo(null);
      resetForm();
      showSuccess('Success', 'Promotion updated');
    } catch (error) {
      showError('Error', 'Failed to update promotion');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPromo) return;

    setIsSaving(true);
    try {
      await deletePromotion(deletingPromo.id);
      setDeletingPromo(null);
      showSuccess('Success', 'Promotion deleted');
    } catch (error) {
      showError('Error', 'Failed to delete promotion');
    } finally {
      setIsSaving(false);
    }
  };

  const handleApply = async () => {
    if (!applyingPromo || !selectedCompanyId) {
      showError('Error', 'Please select a customer');
      return;
    }

    setIsSaving(true);
    try {
      await applyPromotion(applyingPromo.id, selectedCompanyId);
      setShowApplyModal(false);
      setApplyingPromo(null);
      setSelectedCompanyId('');
      showSuccess('Success', 'Promotion applied to customer');
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to apply promotion');
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setPromoForm({
      code: '',
      name: '',
      description: '',
      value: '',
      isActive: true,
    });
  };

  const openEditModal = (promo: Promotion) => {
    setEditingPromo(promo);
    setPromoForm({
      code: promo.code,
      name: promo.name,
      description: promo.description,
      value: promo.value.toString(),
      isActive: promo.isActive,
    });
  };

  const openApplyModal = (promo: Promotion) => {
    setApplyingPromo(promo);
    setShowApplyModal(true);
  };

  const totalCreditsGranted = applications.reduce((sum, app) => sum + app.creditsGranted, 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Promotions</h2>
            <p className="text-slate-500 mt-1">Create and manage promotional credit campaigns</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Promotion
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Active Promotions</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {promotions.filter((p) => p.isActive).length}
                  </p>
                </div>
                <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Tag className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Applications</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {applications.length}
                  </p>
                </div>
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Credits Granted</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {totalCreditsGranted.toLocaleString()}
                  </p>
                </div>
                <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Gift className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Promotions List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : promotions.length > 0 ? (
          <div className="space-y-3">
            {promotions.map((promo) => (
              <Card key={promo.id} className="hover:border-purple-300 transition-colors">
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900">{promo.name}</h3>
                        <Badge variant={promo.isActive ? 'success' : 'default'} size="sm">
                          {promo.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant="info" size="sm">
                          {promo.value} credits
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{promo.description}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span>Code: <strong className="text-slate-700">{promo.code}</strong></span>
                        <span>•</span>
                        <span>Applied: {promo.appliedCount} times</span>
                        <span>•</span>
                        <span>Created: {new Date(promo.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openApplyModal(promo)}
                        disabled={!promo.isActive}
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(promo)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeletingPromo(promo)}
                      >
                        <Trash2 className="h-4 w-4 text-danger-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Tag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">No promotions yet</h3>
                <p className="text-slate-500 mb-4">Create your first promotional campaign</p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Promotion
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal || !!editingPromo}
        onClose={() => {
          setShowCreateModal(false);
          setEditingPromo(null);
          resetForm();
        }}
        title={editingPromo ? 'Edit Promotion' : 'Create Promotion'}
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Promotion Code"
            placeholder="e.g., WELCOME100"
            value={promoForm.code}
            onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
            required
          />
          <Input
            label="Promotion Name"
            placeholder="e.g., Welcome Bonus"
            value={promoForm.name}
            onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })}
            required
          />
          <Input
            label="Description"
            placeholder="Brief description of the promotion"
            value={promoForm.description}
            onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
          />
          <Input
            label="Credit Value"
            type="number"
            placeholder="e.g., 100"
            value={promoForm.value}
            onChange={(e) => setPromoForm({ ...promoForm, value: e.target.value })}
            required
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={promoForm.isActive}
              onChange={(e) => setPromoForm({ ...promoForm, isActive: e.target.checked })}
              className="h-4 w-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-slate-700">
              Active (can be applied to customers)
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setEditingPromo(null);
                resetForm();
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={editingPromo ? handleUpdate : handleCreate}
              isLoading={isSaving}
            >
              {editingPromo ? 'Update' : 'Create'} Promotion
            </Button>
          </div>
        </div>
      </Modal>

      {/* Apply Promotion Modal */}
      <Modal
        isOpen={showApplyModal}
        onClose={() => {
          setShowApplyModal(false);
          setApplyingPromo(null);
          setSelectedCompanyId('');
        }}
        title="Apply Promotion"
        size="md"
      >
        {applyingPromo && (
          <div className="space-y-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm font-medium text-purple-900 mb-1">{applyingPromo.name}</p>
              <p className="text-sm text-purple-700">{applyingPromo.code} - {applyingPromo.value} credits</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Customer
              </label>
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Choose a customer...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name} ({company.credits} credits)
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button
                variant="outline"
                onClick={() => {
                  setShowApplyModal(false);
                  setApplyingPromo(null);
                  setSelectedCompanyId('');
                }}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button onClick={handleApply} isLoading={isSaving}>
                Apply Promotion
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingPromo}
        onClose={() => setDeletingPromo(null)}
        title="Delete Promotion"
        size="sm"
      >
        {deletingPromo && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Are you sure you want to delete <strong>{deletingPromo.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setDeletingPromo(null)} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete} isLoading={isSaving}>
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
}
